import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { RepositoryError } from '@application/ports';
import type { Position } from '@domain/entities/RuntimeConfig';
import {
  cleanPositionTitle,
  findPositionByTitle,
  isPositionTitleValid,
  searchPositions,
  sortPositions,
} from '@domain/policies/PositionCatalog';
import { getServices } from '@infrastructure/services';
import { cx, inputClass } from '../design-system';
import { useToast } from '../providers/ToastProvider';
import styles from './PositionPicker.module.css';

/**
 * Elegir un puesto: se busca escribiendo, y si no está, se agrega ahí mismo.
 *
 * Sustituye a una lista desplegable con los 322 puestos del catálogo en el orden
 * en que llegaron de las cargas del padrón —ninguno—. La pidió JC el 13 oct 2026,
 * con alguien a media alta buscando «Directora de Marketing», que además no
 * existía: ordenada, con buscador y con un «+».
 *
 * **Lo que ya estaba escrito en el formulario no se toca.** El catálogo llega de
 * la configuración viva; este componente sólo recibe la lista y avisa del id.
 *
 * **Un puesto recién agregado se enseña sin esperar a la configuración.** En una
 * red que bloquea el canal de escucha el catálogo nuevo puede tardar un minuto
 * en llegar; mientras, el formulario diría «elige un puesto» sobre el que la
 * persona acaba de crear. Por eso se guarda aquí lo que devolvió el servidor
 * hasta que la lista de verdad lo traiga.
 */

export interface PositionPickerProps {
  /** El id del control, para que la etiqueta de `Field` apunte a él. */
  readonly id: string;
  /** El catálogo entero. Aquí se decide cuáles se ofrecen y en qué orden. */
  readonly positions: readonly Position[];
  /** El id elegido, o `''` si todavía no hay ninguno. */
  readonly value: string;
  readonly onChange: (positionId: string) => void;
  readonly disabled?: boolean;
  /**
   * Si quien mira puede dar de alta un puesto. Lo decide quien monta el
   * componente —con `useAccess`—; el servidor lo vuelve a comprobar.
   */
  readonly canAdd: boolean;
}

const reasonOf = (error: RepositoryError): string =>
  'what' in error ? error.what : 'detail' in error ? error.detail : 'Inténtalo de nuevo.';

export const PositionPicker = ({
  id,
  positions,
  value,
  onChange,
  disabled = false,
  canAdd,
}: PositionPickerProps): ReactNode => {
  const toast = useToast();
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const popover = useRef<HTMLDivElement>(null);

  /** `null`: no se está buscando, y la caja enseña el puesto elegido. */
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  /** Lo que se agregó aquí y la configuración viva todavía no trae. */
  const [added, setAdded] = useState<readonly Position[]>([]);

  /*
   * Los que se ofrecen: los encendidos, más el que la ficha ya tiene aunque se
   * haya retirado —quitarlo de la lista haría que la caja saliera vacía sobre
   * alguien que sí tiene puesto—, más lo recién agregado. Por título.
   */
  const offered = useMemo(() => {
    const known = new Set(positions.map((position) => position.id));
    return sortPositions([
      ...positions.filter((position) => position.enabled || position.id === value),
      ...added.filter((position) => !known.has(position.id)),
    ]);
  }, [positions, added, value]);

  const selected = offered.find((position) => position.id === value) ?? null;
  const typed = query ?? '';
  const matches = useMemo(() => searchPositions(offered, typed), [offered, typed]);

  /* Se puede agregar lo escrito cuando es un título de verdad y no hay ya un
     puesto que se llame así —aunque esté con otros acentos o mayúsculas—. */
  const title = cleanPositionTitle(typed);
  const addable =
    canAdd && isPositionTitleValid(title) && findPositionByTitle(offered, title) === null;

  /* La fila de agregar cuenta como una opción más, la última, para el teclado. */
  const optionCount = matches.length + (addable ? 1 : 0);
  const activeIndex = optionCount === 0 ? -1 : Math.min(active, optionCount - 1);
  const optionId = (index: number): string => `${listId}-o${String(index)}`;

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    /* jsdom no lo tiene, y un navegador viejo tampoco: sin él la lista sigue
       funcionando, sólo que no acompaña a las flechas. */
    const node = document.getElementById(`${listId}-o${String(activeIndex)}`);
    if (node !== null && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex, listId]);

  /*
   * Al abrirse, la lista se trae a la vista. El campo suele quedar al pie del
   * área con scroll de un modal, y sin esto la lista se abría debajo del borde:
   * se veía un renglón y había que adivinar que seguía.
   */
  useEffect(() => {
    if (!open) return;
    const node = popover.current;
    if (node !== null && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  const close = (): void => {
    setOpen(false);
    setQuery(null);
    setActive(0);
  };

  const choose = (position: Position): void => {
    onChange(position.id);
    close();
  };

  const add = async (): Promise<void> => {
    if (!addable || busy) return;
    setBusy(true);
    const services = getServices();
    const result = await services.config.addPosition(title);
    setBusy(false);

    if (!result.ok) {
      toast.bad(`No se pudo agregar el puesto. ${reasonOf(result.error)}`);
      return;
    }
    const { position, created } = result.value;
    setAdded((current) => [...current.filter((entry) => entry.id !== position.id), position]);
    choose(position);
    /* En demo no hay catálogo donde guardarlo, y decir «se agregó» sería la
       misma mentira que ya costó una llave pegada en la pestaña del asistente. */
    if (services.isDemo) {
      toast.info(`Modo demo: «${position.title}» sólo existe en esta pestaña; no se guardó.`);
      return;
    }
    toast.ok(
      created
        ? `«${position.title}» se agregó al catálogo de puestos.`
        : `«${position.title}» ya estaba en el catálogo.`,
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (optionCount === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((activeIndex + step + optionCount) % optionCount);
      return;
    }
    if (event.key === 'Enter') {
      /* Nunca envía el formulario: un Enter aquí es elegir, o no es nada. */
      event.preventDefault();
      if (!open || activeIndex < 0) return;
      const match = matches[activeIndex];
      if (match !== undefined) choose(match);
      else void add();
      return;
    }
    if (event.key === 'Escape' && open) {
      /* Cierra la lista y sólo la lista: sin esto se llevaría el modal, con
         todo lo escrito dentro. */
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  /* El «+» de siempre a la vista: con algo escrito que se puede agregar, lo
     agrega; si no, lleva a la caja y explica cómo. */
  const onPlus = (): void => {
    if (addable) {
      void add();
      return;
    }
    setOpen(true);
    setQuery((current) => current ?? '');
    input.current?.focus();
  };

  return (
    <div
      ref={root}
      className={styles.picker}
      onBlur={(event) => {
        /* Sólo cuando el foco sale del componente entero: pasar de la caja al
           «+» no es irse. */
        if (root.current?.contains(event.relatedTarget) === true) return;
        close();
      }}
    >
      <div className={styles.row}>
        <input
          ref={input}
          id={id}
          type="text"
          role="combobox"
          className={cx(inputClass(), styles.input)}
          value={query ?? selected?.title ?? ''}
          placeholder={disabled ? 'Sin puesto' : 'Busca un puesto…'}
          disabled={disabled}
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          onFocus={(event) => {
            setOpen(true);
            /* Con un puesto ya elegido, lo que se teclee lo reemplaza. */
            event.currentTarget.select();
          }}
          onClick={() => {
            setOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
        {canAdd && !disabled && (
          <button
            type="button"
            className={styles.plus}
            aria-label={addable ? `Agregar «${title}» como puesto nuevo` : 'Agregar un puesto nuevo'}
            title="Agregar un puesto nuevo"
            disabled={busy}
            onClick={onPlus}
          >
            +
          </button>
        )}
      </div>

      {open && !disabled && (
        <div ref={popover} className={styles.popover}>
          <ul id={listId} role="listbox" aria-label="Puestos" className={styles.list}>
            {matches.map((position, index) => (
              <li
                key={position.id}
                id={optionId(index)}
                role="option"
                aria-selected={position.id === value}
                className={cx(
                  styles.option,
                  index === activeIndex && styles.optionActive,
                  position.id === value && styles.optionSelected,
                )}
                /* `mousedown` y no `click`: el clic llegaría después del `blur`
                   de la caja, con la lista ya cerrada. */
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(position);
                }}
                onMouseEnter={() => {
                  setActive(index);
                }}
              >
                {position.title}
              </li>
            ))}

            {matches.length === 0 && (
              <li role="presentation" className={styles.empty}>
                {typed.trim().length === 0
                  ? 'El catálogo de puestos está vacío.'
                  : `Ningún puesto coincide con «${title}».`}
              </li>
            )}

            {/* Agregar es la última opción de la lista, no un botón aparte: así
                las flechas llegan a ella y Enter la elige. */}
            {addable && (
              <li
                id={optionId(matches.length)}
                role="option"
                aria-selected={false}
                aria-disabled={busy}
                className={cx(styles.addRow, activeIndex === matches.length && styles.optionActive)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  void add();
                }}
                onMouseEnter={() => {
                  setActive(matches.length);
                }}
              >
                <span className={styles.addMark} aria-hidden="true">
                  +
                </span>
                {busy ? 'Agregando…' : `Agregar «${title}» como puesto nuevo`}
              </li>
            )}
          </ul>

          <p className={styles.foot}>
            {typed.trim().length === 0
              ? `${String(offered.length)} puestos, por orden alfabético. Escribe para buscar.`
              : `${String(matches.length)} de ${String(offered.length)} puestos.`}
            {canAdd && !addable && typed.trim().length === 0 && ' Si no está, escríbelo y pulsa +.'}
          </p>
        </div>
      )}
    </div>
  );
};
